import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PermissionEnum } from '@app/core/services/administrator/models/enum/permission.enum';
import { Page } from '@app/core/services/commons/page';
import { InvoiceMetaData } from '@app/core/services/invoice/models/invoice-meta-data.model';
import { PaymentMethodEnum } from '@app/core/services/subscription/models/enum/payment-mode.enum';
import { SubscriptionDTO } from '@app/core/services/subscription/models/subscription/subscription.model';
import { AddSubscriptionHistoryPopupComponent } from './add-subscription-history-popup/add-subscription-history-popup.component';
import { SubscriptionService } from '@app/core/services/subscription/subscription.service';
import { SubscriptionStatusesHistory } from '@app/core/services/subscription/models/subscription/subscription-status-history.model';
import { NotificationService } from '@app/core/services/util/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { SubscriptionStatusEnum } from '@app/core/services/subscription/models/enum/subscription-status.enum';
import { AdministratorService } from '@app/core/services/administrator/administrator.service';
import { UserAdminUI } from '@app/core/services/administrator/models/user-admin.model';

@Component({
    selector: 'ap-subscription-details-content',
    templateUrl: './subscription-details-content.component.html',
    styleUrls: ['./subscription-details-content.component.css'],
    standalone: false
})
export class SubscriptionDetailsContentComponent implements OnInit {
  @Input() subscription: SubscriptionDTO;
  @Input() invoices: Page<InvoiceMetaData>;
  @Input() userView = false;
  @Input() isPayer = false;

  canUpdateSubscriptionActive = false;
  canUpdateSubscriptionTerminated = false;

  _lastMonthActivities: number;
  @Input()
  set lastMonthActivities(value: number) {
    this._lastMonthActivities = value;
  };

  get lastMonthActivities() {
    return this._lastMonthActivities;
  };

  _monthActivities: number;
  @Input()
  set monthActivities(value: number) {
    this._monthActivities = value;
  };

  get monthActivities() {
    return this._monthActivities;
  };

  @Output() subscriptionChange = new EventEmitter<SubscriptionDTO>();

  Permissions = PermissionEnum;
  PaymentMode = PaymentMethodEnum;
  SubscriptionStatus = SubscriptionStatusEnum;

  canShowPenIcon = false;
  canShowTrashIcon = false;

  constructor(
    public dialog: MatDialog,
    private subscriptionService: SubscriptionService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private administratorService: AdministratorService
  ) {}

  ngOnInit(): void {
    const loggedAgent = this.administratorService.loggedAgent$.getValue() as UserAdminUI;
    if (loggedAgent && loggedAgent.roleInfos && loggedAgent.roleInfos.permissions) {
      this.canUpdateSubscriptionActive = loggedAgent.roleInfos.permissions.includes(PermissionEnum.UPDATE_SUBSCRIPTION_ACTIVE as any);
      this.canUpdateSubscriptionTerminated = loggedAgent.roleInfos.permissions.includes(PermissionEnum.UPDATE_SUBSCRIPTION_TERMINATED as any);
    }
    this.setPenIconVisibility();
  }

  ngOnChanges(): void {
    this.setPenIconVisibility();
  }

  private setPenIconVisibility(): void {
    if (!this.subscription) return;
    const status = this.subscription.status;
    const activeOrInactive = status === SubscriptionStatusEnum.ACTIVE || status === SubscriptionStatusEnum.INACTIVE;

    this.canShowPenIcon = (this.canUpdateSubscriptionActive && activeOrInactive) || (this.canUpdateSubscriptionTerminated && activeOrInactive);
  }

  openPlannedChangeStatusDialog() {
    if (!this.canShowPenIcon) {
        this.notificationService.notify('error', 'Unauthorized access.');
        return;
    }

    const dialogRef = this.dialog.open(AddSubscriptionHistoryPopupComponent, {
      panelClass: 'modal',
      width: '40%',
      data: {
        canUpdateSubscriptionActive: this.canUpdateSubscriptionActive,
        canUpdateSubscriptionTerminated: this.canUpdateSubscriptionTerminated
      }
    })

    dialogRef.afterClosed().subscribe(data => {
      if (data) {
        if (data.isDeferred) {
          this.subscriptionService.addSubscriptionStatusHistory(this.subscription.subscriptionId, data.status, data.effectiveDate, data.reason).subscribe({
            next: (subscriptionStatusHistory: SubscriptionStatusesHistory) => {
              this.subscription = {
                ...this.subscription,
                subscriptionStatusesHistory: [ ...this.subscription.subscriptionStatusesHistory, subscriptionStatusHistory]
              }
              this.subscriptionChange.emit(this.subscription);
              this.notificationService.notify('success', this.translateService.instant('SUBSCRIPTIONS.SUBSCRIPTION_DETAILS.CHANGE_STATUS.TOAST.ADD_DIFFERED.SUCCESS'))
            },
            error: _err => {
              if (_err.error.code === 403) {
                if (_err.error.message.includes('A planned status change to status')) {
                  this.notificationService.notify('error', this.translateService.instant('SUBSCRIPTIONS.SUBSCRIPTION_DETAILS.CHANGE_STATUS.TOAST.ADD_DIFFERED.ERROR_ALREADY_EXIST'))
                  return;
                }
              }
              this.notificationService.notify('error', this.translateService.instant('SUBSCRIPTIONS.SUBSCRIPTION_DETAILS.CHANGE_STATUS.TOAST.ADD_DIFFERED.ERROR_GLOBAL'))
            }
          });
        } else {
          this.subscriptionService.updateSubscriptionStatus(data.status, this.subscription.subscriptionId, data.reason).subscribe({
            next: (subscription: SubscriptionDTO) => {
              this.subscription = {
                ...this.subscription,
                subscriptionStatusesHistory: [ ...subscription.subscriptionStatusesHistory]
              }

              this.subscription.status = subscription.status;

              this.subscriptionChange.emit(this.subscription);
              this.notificationService.notify('success', this.translateService.instant('SUBSCRIPTIONS.SUBSCRIPTION_DETAILS.CHANGE_STATUS.TOAST.ADD.SUCCESS'))
            },
            error: _err => {
              if (_err.error.code === 403) {
                if (_err.error.message.includes('has invalid status')) {
                  this.notificationService.notify('error', this.translateService.instant('SUBSCRIPTIONS.SUBSCRIPTION_DETAILS.CHANGE_STATUS.TOAST.ADD.ERROR_SAME_STATUS'))
                  return;
                }
              }
              this.notificationService.notify('error', this.translateService.instant('SUBSCRIPTIONS.SUBSCRIPTION_DETAILS.CHANGE_STATUS.TOAST.ADD.ERROR_GLOBAL'))
            }
          });
        }
      }
    });
  }
}
